import { useMemo } from "react";
import { LiFiWidget } from "@lifi/widget";
import { useTheme } from "../contexts/ThemeContext";

// Fee recipient wallets — submit all three to Li.Fi when registering:
//   EVM   : 0x9D9BE7A5DbFdF5d80668F399BAD59527C2eE5575
//   Solana: BVqmBtNyPjZWuKNmfvSkGRsWQt9sH6NKWGp2YdFtb9Nn
//   Tron  : 0x5e2F01f4438dbC1Ed934a915cf80E3243a39Ed5c
//
// To enable fee collection:
//   1. Register at https://li.fi/integrators with integrator name "dexora"
//   2. Submit all 3 wallets above as fee recipients per chain type
//   3. Once activated, uncomment feeConfig below

const LiFiBridge = () => {
  const { theme } = useTheme();
  const isDark = theme !== "light";

  const widgetConfig = useMemo(
    () => ({
      integrator: "Dexora",

      apiKey: process.env.REACT_APP_LIFI_API_KEY,
      feeConfig: { fee: 0.001,showFeePercentage:false },

      // Disable EIP-7702 bundled transactions — prevents MetaMask bundle errors
      useRelayerRoutes: false,

      appearance: isDark ? "dark" : "light",

      variant: "wide",

      hiddenUI: ["poweredBy", "walletMenu"],

      theme: {
        palette: {
          primary: {
            main: isDark ? "#D4A843" : "#B8892B",
            contrastText: isDark ? "#0A0A0F" : "#FFFFFF",
          },
          secondary: {
            main: isDark ? "#D4A843" : "#B8892B",
          },
          text: {
            primary: isDark ? "#FFFFFF" : "#1A1206",
            secondary: isDark ? "rgba(255,255,255,0.6)" : "rgba(26,18,6,0.6)",
          },
          background: {
            paper: isDark ? "#111117" : "#FFFFFF",
            default: isDark ? "#0A0A0F" : "#FBF6EC",
          },
          grey: {
            800: isDark ? "#1E1E28" : "#F0DDB8",
          },
        },
        shape: {
          borderRadius: 16,
          borderRadiusSecondary: 12,
        },
        typography: {
          fontFamily: "inherit",
        },
        container: {
          boxShadow: isDark
            ? "0 0 0 1px rgba(212,168,67,0.1)"
            : "0 0 0 1px rgba(184,137,43,0.15)",
          borderRadius: "16px",
        },
      },
      components: {
        MuiIconButton: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? "rgba(212,168,67,0.1)" : "rgba(184,137,43,0.1)",
              color: isDark ? "#D4A843" : "#B8892B",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: isDark ? "rgba(212,168,67,0.2)" : "rgba(184,137,43,0.2)",
              },
            },
          },
        },
      },
    }),
    [isDark]
  );

  return (
    <div className="w-full max-w-[900px] px-3 sm:px-6 pb-20 md:pb-10 pt-6 sm:pt-0 overflow-x-hidden">
      <div className="flex flex-col gap-5">

        {/* Page Header */}
        <div className="flex flex-col items-center text-center gap-3 py-4">
          <p className="text-[11px] font-semibold text-dexora-cyan uppercase tracking-[0.18em]">Cross-Chain</p>
          <h1 className="text-[24px] sm:text-[28px] font-bold text-dexora-text-primary leading-tight">
            Bridge Assets <span className="gradient-text">Across Any Chain</span>
          </h1>
          <p className="text-[12px] text-dexora-text-muted max-w-md">
            Transfer tokens across 20+ networks. Powered by Li.Fi — optimal routes, lowest fees.
          </p>
          {/* <div className="inner-3d px-4 py-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-[12px] font-semibold text-emerald-400">Live</span>
          </div> */}
        </div>

        {/* Stats Row */}
        {/* <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Networks", value: "20+" },
            { label: "Protocol", value: "Li.Fi" },
            { label: "Blockchains", value: "40+" },
          ].map((stat, i) => (
            <div key={i} className="inner-3d p-3 flex flex-col items-center gap-1">
              <span className="text-[16px] font-bold text-dexora-cyan">{stat.value}</span>
              <span className="text-[10px] text-dexora-text-muted font-medium uppercase tracking-wider">{stat.label}</span>
            </div>
          ))}
        </div> */}

        {/* LiFi Widget */}
        <div
          className="w-full relative card-3d"
          style={{
            padding: "16px",
            paddingBottom: "24px",
            boxSizing: "border-box",
            borderRadius: "16px",
            overflow: "hidden",
            background: isDark ? "#111117" : "#ffffff",
            backgroundImage: "none",
          }}
        >
          <LiFiWidget key={isDark ? "dark" : "light"} config={widgetConfig} integrator="Dexora" />
        </div>

        {/* How It Works */}
        <div className="card-3d p-5">
          <p className="text-[11px] font-semibold text-dexora-text-muted uppercase tracking-widest mb-3">How it works</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { step: "1", title: "Select Chains", desc: "Pick your source and destination blockchain networks" },
              { step: "2", title: "Pick Tokens", desc: "Choose the token to send and what you want to receive" },
              { step: "3", title: "Confirm & Bridge", desc: "Review the best route, confirm the transaction, receive tokens on the destination chain" },
            ].map((item, i) => (
              <div key={i} className="pool-card-3d p-4 flex flex-col gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-dexora-cyan/10 text-dexora-cyan text-[12px] font-bold">
                  {item.step}
                </div>
                <p className="text-[13px] font-bold text-dexora-text-primary">{item.title}</p>
                <p className="text-[11px] text-dexora-text-muted leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features + Networks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          {/* Why Use */}
          <div className="card-3d p-4">
            <p className="text-[11px] font-semibold text-dexora-text-muted uppercase tracking-widest mb-3">Why Dexora Bridge</p>
            <div className="flex flex-col gap-2">
              {[
                {
                  icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>,
                  title: "Multi-Chain Support",
                  desc: "Ethereum, BSC, Polygon, Arbitrum, Avalanche, Solana & 15+ more",
                },
                {
                  icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
                  title: "Best Route Aggregation",
                  desc: "Optimal path across DEXes and bridges for lowest fees",
                },
                {
                  icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                  title: "Audited & Secure",
                  desc: "Battle-tested Li.Fi protocol securing billions in volume",
                },
                {
                  icon: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                  title: "Fast Settlement",
                  desc: "Cross-chain transfers complete in minutes with live tracking",
                },
              ].map((item, i) => (
                <div key={i} className="pool-card-3d p-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-dexora-cyan/10 text-dexora-cyan shrink-0 mt-0.5">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[12px] font-bold text-dexora-text-primary">{item.title}</p>
                    <p className="text-[11px] text-dexora-text-muted leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Supported Networks */}
          <div className="card-3d p-4">
            <p className="text-[11px] font-semibold text-dexora-text-muted uppercase tracking-widest mb-3">Supported Networks</p>
            <div className="flex flex-wrap gap-2">
              {["Ethereum", "BSC", "Polygon", "Arbitrum", "Optimism", "Avalanche", "Solana", "Base", "Linea", "Mantle", "zkSync", "Story"].map((n, i) => (
                <span key={i} className="token-pill-3d px-2.5 py-1 text-[11px] font-medium text-dexora-text-secondary">{n}</span>
              ))}
              <span className="token-pill-3d px-2.5 py-1 text-[11px] font-medium text-dexora-cyan">+8 more</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LiFiBridge;
